import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import {
	TextField,
	Button,
	Box,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	FormControl,
	FormHelperText,
	Dialog,
	DialogTitle,
	IconButton,
	DialogContent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import validate from '../../../../../components/component/resused-component/Validation3';
import { getAllEvents } from '../../../../../services/api/MenuAPI';
import { customizedMenuAPI } from '../../../../../services/api/UserAPI';

interface FormData {
	name: string;
	email: string;
	mobile: string;
	eventType: string;
	noOfPeople: string;
	address: string;
	area: string;
	functionDate: Dayjs | null;
}
interface EventTypes {
	_id: string;
	eventType: string;
	menuType: string[];
}
interface Errors {
	name?: string;
	email?: string;
	mobile?: string;
	eventType?: string;
	noOfPeople?: string;
	address?: string;
	area?: string;
	functionDate?: string;
}

interface QuotationForm {
	open: boolean;
	handleClose: () => void;
	onEventIdChange: (eventId: string) => void;
}

const GetQuotationForm: React.FC<QuotationForm> = ({ open, handleClose, onEventIdChange }) => {
	const [formData, setFormData] = useState<FormData>({
		name: '',
		email: '',
		mobile: '',
		eventType: '',
		noOfPeople: '50',
		address: '',
		area: '',
		functionDate: null,
	});
	const navigate = useNavigate();
	const [eventTypesData, setEventTypesData] = useState<EventTypes[]>([]);
	const [errors, setErrors] = useState<Errors>({});
	const [loadingData, setLoadingData] = useState(false);
	const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});

		const fieldErrors = validate({ ...formData, [name]: value });
		if (!fieldErrors[name as keyof FormData]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: undefined,
			}));
		}
	};

	const handleSelectChange = (e: SelectChangeEvent<string>) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});

		const fieldErrors = validate({ ...formData, [name]: value });
		if (!fieldErrors[name as keyof FormData]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: undefined,
			}));
		}
	};

	// const handleSubmit = async (e: FormEvent) => {
	// 	e.preventDefault();
	// 	const validationErrors = validate(formData);
	// 	if (Object.keys(validationErrors).length === 0) {
	// 		try {
	// 			selLoadingData(true);
	// 			const response = await customizedMenuAPI(formData);
	// 			console.log('Form data submitted:', formData);
	// 			sessionStorage.setItem('quotationFormData', JSON.stringify(formData));
	// 			onEventIdChange(formData.eventType);
	// 			handleClose();
	// 		} catch (error) {
	// 			console.error('Error submitting form:', error);
	// 		} finally {
	// 			selLoadingData(false);
	// 		}
	// 	} else {
	// 		setErrors(validationErrors);
	// 	}
	// };
	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		const validationErrors = validate(formData);
		if (Object.keys(validationErrors).length === 0) {
			const storedFormData = sessionStorage.getItem('quotationFormData');
			if (!storedFormData) {
				try {
					setLoadingData(true);
					const backendData = {
						name: formData.name,
						email: formData.email,
						mobile: formData.mobile,
						eventType:
							eventTypesData.find((event) => event._id === formData.eventType)
								?.eventType || '',
						functionDate: formData.functionDate?.format('YYYY-MM-DD') || '',
						location: `${formData.address}, ${formData.area}`,
					};
					const response = await customizedMenuAPI(backendData);
					console.log('Form data submitted:', response);
					sessionStorage.setItem('quotationFormData', JSON.stringify(formData));
					onEventIdChange(formData.eventType);
					handleClose();
				} catch (error) {
					console.error('Error submitting form:', error);
				} finally {
					setLoadingData(false);
				}
			} else {
				console.log('Form data already submitted.');
				onEventIdChange(formData.eventType);
				handleClose();
			}
		} else {
			setErrors(validationErrors);
		}
	};
	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await getAllEvents();
				if (response.success) {
					setEventTypesData(response.data.eventTypes);
				}
			} catch (error) {
				console.log(error);
			}
		};

		const storedFormData = sessionStorage.getItem('quotationFormData');
		if (storedFormData) {
			const parsedData = JSON.parse(storedFormData);
			setFormData({
				...formData,
				...parsedData,
				functionDate: parsedData.functionDate ? dayjs(parsedData.functionDate) : null,
			});
		}

		fetchData();
	}, []);
	const handleNavigate = () => {
		navigate('/menu');
	};

	const handleClear = () => {
		sessionStorage.removeItem('quotationFormData');
		navigate('/menu');
	};

	return (
		<Box>
			<Dialog
				open={open}
				fullWidth
				maxWidth="sm"
				slotProps={{
					backdrop: {
						sx: {
							backgroundColor: 'rgba(0, 0, 0, 0.74)',
						},
					},
				}}
			>
				<DialogTitle fontWeight={500} fontSize={'20px'} textAlign={'center'}>
					Get Quotation
					<IconButton
						aria-label="close"
						onClick={handleNavigate}
						sx={{ position: 'absolute', right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box
							component="form"
							onSubmit={handleSubmit}
							noValidate
							sx={{
								width: '100%',
								p: 2,
							}}
						>
							<Grid container spacing={2} justifyContent="center" alignItems="center">
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<TextField
										color="secondary"
										fullWidth
										label="Name"
										name="name"
										value={formData.name}
										onChange={handleTextFieldChange}
										error={!!errors.name}
										helperText={errors.name}
										size="small"
										variant="outlined"
										// slotProps={{
										// 	input: {
										// 		sx: {
										// 			fontSize: '14px', // <-- input text size
										// 		},
										// 	},
										// }}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<TextField
										color="secondary"
										fullWidth
										label="Email"
										name="email"
										value={formData.email}
										onChange={handleTextFieldChange}
										error={!!errors.email}
										helperText={errors.email}
										size="small"
										variant="outlined"
										// slotProps={{
										// 	input: {
										// 		sx: {
										// 			fontSize: '14px', // <-- input text size
										// 		},
										// 	},
										// }}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<TextField
										color="secondary"
										fullWidth
										label="Mobile"
										name="mobile"
										value={formData.mobile}
										error={!!errors.mobile}
										helperText={errors.mobile}
										size="small"
										onChange={(e) => {
											const value = e.target.value;
											if (/^\d*$/.test(value)) {
												const sanitizedValue = value.replace(/^0+/, ''); // Remove leading zeros

												setFormData((prev) => ({
													...prev,
													mobile: sanitizedValue,
												}));

												const fieldErrors = validate({
													...formData,
													mobile: sanitizedValue,
												});

												if (!fieldErrors.mobile) {
													setErrors((prevErrors) => ({
														...prevErrors,
														mobile: undefined,
													}));
												}
											}
										}}
										variant="outlined"
										slotProps={{
											// input: {
											// 	sx: {
											// 		fontSize: '14px',
											// 	},
											// },
											htmlInput: {
												inputMode: 'numeric',
												pattern: '[0-9]*',
												maxLength: 10, // optional: limit to 10 digits
											},
										}}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<FormControl
										fullWidth
										error={!!errors.eventType}
										size="small"
										color="secondary"
									>
										<InputLabel id="demo-simple-select-label">
											Event Type
										</InputLabel>
										<Select
											color="secondary"
											name="eventType"
											labelId="demo-simple-select-label"
											id="demo-simple-select"
											value={formData.eventType}
											onChange={handleSelectChange}
											label="Event Type"
											variant="outlined"
											// slotProps={{
											// 	input: {
											// 		sx: {
											// 			fontSize: '14px', // <-- input text size
											// 		},
											// 	},
											// }}
										>
											<MenuItem value="" disabled>
												Select Event Type
											</MenuItem>
											{eventTypesData.map((value) => (
												<MenuItem key={value.eventType} value={value._id}>
													{value.eventType}
												</MenuItem>
											))}
										</Select>
										{errors.eventType && (
											<FormHelperText>{errors.eventType}</FormHelperText>
										)}
									</FormControl>
								</Grid>
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<DatePicker
										label="Date"
										value={formData.functionDate}
										minDate={dayjs().add(1, 'day')}
										onChange={(newValue: Dayjs | null) => {
											setFormData((prevFormData) => ({
												...prevFormData,
												functionDate: newValue,
											}));
											const fieldErrors = validate({
												...formData,
												functionDate: newValue,
											});
											if (!fieldErrors.functionDate) {
												setErrors((prevErrors) => ({
													...prevErrors,
													functionDate: undefined,
												}));
											}
										}}
										slotProps={{
											textField: {
												fullWidth: true,
												error: !!errors.functionDate,
												helperText: errors.functionDate,
												size: 'small',
												sx: {
													'& .MuiInputBase-input': {
														fontSize: '14px', // input field text
													},
												},
											},
											popper: {
												placement: 'auto-start',
											},
										}}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<TextField
										color="secondary"
										fullWidth
										type="number"
										label="No of People"
										name="noOfPeople"
										value={formData.noOfPeople}
										onChange={(e) => {
											const value = e.target.value;
											if (/^\d*$/.test(value)) {
												setFormData({
													...formData,
													noOfPeople: value,
												});
											}
										}}
										onBlur={() => {
											const numericValue = Number(formData.noOfPeople);
											if (formData.noOfPeople !== '' && numericValue < 50) {
												setFormData({
													...formData,
													noOfPeople: '50',
												});
											}
										}}
										error={!!errors.noOfPeople}
										helperText={errors.noOfPeople}
										size="small"
										variant="outlined"
										// slotProps={{
										// 	input: {
										// 		sx: {
										// 			fontSize: '14px',
										// 		},
										// 	},
										// }}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<TextField
										color="secondary"
										fullWidth
										label="Mandapam Name"
										name="address"
										value={formData.address}
										onChange={handleTextFieldChange}
										error={!!errors.address}
										helperText={errors.address}
										size="small"
										variant="outlined"
										// slotProps={{
										// 	input: {
										// 		sx: {
										// 			fontSize: '14px', // <-- input text size
										// 		},
										// 	},
										// }}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
									<TextField
										color="secondary"
										fullWidth
										label="Area"
										name="area"
										value={formData.area}
										onChange={handleTextFieldChange}
										error={!!errors.area}
										helperText={errors.area}
										size="small"
										placeholder="e.g. Avadi"
										variant="outlined"
									/>
								</Grid>
								<Grid size={{ xs: 12 }} sx={{ textAlign: 'center' }}>
									<Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
										<Button
											onClick={handleClear}
											variant="outlined"
											color="secondary"
											sx={{ width: '40%' }}
											loading={loadingData}
											loadingIndicator="Sending..."
											className="me-2"
										>
											Close
										</Button>
										<Button
											type="submit"
											variant="contained"
											color="primary"
											sx={{ width: '40%' }}
											loading={loadingData}
											loadingIndicator="Sending..."
										>
											Choose Menu
										</Button>
									</Box>
								</Grid>
							</Grid>
						</Box>
					</LocalizationProvider>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default GetQuotationForm;
